import { AnalyticsPayload, renderTemplate, SB } from '@play-co/replicant';
import { ChatbotAssets } from '@play-co/replicant/lib/cli/ReplicantChatbot';
import {
  ChatbotMessageInstance,
  TemplateRendererAPI,
} from '@play-co/replicant/lib/core/ReplicantChatbot';
import { getLineRedirectURL } from 'src/replicant/utils/payload';
import { chatbotAssets, chatbotMessageTemplates } from '../messageTemplates';
import { FlexMessagesArgs, FlexMessagesTemplate } from './index';
import {
  Action,
  FlexBubble,
  FlexCarousel,
  FlexContainer,
  FlexMessage,
  Message,
} from './types';
import { ChatbotMessagePayload } from '../chatbotMessage';

/**
 * Action argument for single message
 */
export type ChatbotFlexMessageArgs = {
  template: FlexMessagesTemplate;
  payload: ChatbotMessagePayload;
  data: FlexMessagesArgs[FlexMessagesTemplate];
  analyticsUserProperties?: {
    [key: string]: unknown;
  };
};

// This trick will make type checker happy and will allow get all types in place
type ChatbotFlexAssets = typeof chatbotAssets | ChatbotAssets;

/**
 * This argument will be sent to render function
 */
export type FlexMessageTemplate<T> = (
  data: T,
  payload: AnalyticsPayload,
  api: TemplateRendererAPI<ChatbotFlexAssets>,
) => Message;

/**
 * Use this function for creating other messages templates
 *
 * @example
 * export const demoMessages = {
 *    id_of_message_template: createFlexMessageTemplate(SB.object({}) ,
 *      (data: typeof SB.object({}), payload: AnalyticsPayload, api: TemplateRendererAPI) => {
 *        return '{ ...flex template... }';
 *      }
 * )};
 */
export function createFlexMessageTemplate<T>(
  templateData: SB.Schema<T>,
  flexMessageTemplateGenerator: FlexMessageTemplate<T>,
) {
  return renderTemplate({
    args: templateData,
    renderers: {
      line: ({ api, payload, args }) =>
        flexMessageTemplateGenerator(args, payload, api),
    },
  });
}

/**
 * Create flex container
 */
export function createFlexContainer(
  altText: string,
  container: FlexContainer,
): FlexMessage {
  return {
    type: 'flex',
    altText,
    contents: container,
  };
}

/**
 * Create flex carousel container
 */
export function createCarouselContainer(bubbles: FlexBubble[]): FlexCarousel {
  return {
    type: 'carousel',
    contents: bubbles,
  };
}

/**
 * Create open game action
 */
export function createOpenGameAction(altText: string, payload: object): Action {
  // Action label limited by 40 characters
  const label = altText.length > 40 ? `${altText.substring(0, 38)}…` : altText;

  return {
    type: 'uri',
    label,
    uri: getLineRedirectURL(payload),
  };
}

/**
 * Get message data for api.chatbot.sendMessage
 */
export function getChatbotFlexMessage(args: ChatbotFlexMessageArgs) {
  const { template, data, payload, analyticsUserProperties } = args;

  return chatbotMessageTemplates[template]({
    args: (data as FlexMessagesArgs[typeof template]) as any,
    payload,
    analyticsUserProperties,
  }) as ChatbotMessageInstance<typeof data>;
}
